// Subscription Plans
export type SubscriptionPlan = 'free' | 'basic' | 'pro';

// Subscription Status
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';

// Trial configuration
export const TRIAL_DURATION_DAYS = 3;

// Payment Provider
export type PaymentProvider = 'stripe' | 'paypal' | null;

// Billing Cycle
export type BillingCycle = 'monthly' | 'annual';

// Region for pricing
export type PricingRegion = 'ca';

// Currency
export type Currency = 'CAD';

// Feature Keys for gating
export type FeatureKey =
  | 'unlimited_recordings'
  | 'ai_coach'
  | 'phoneme_analysis'
  | 'prosody_analysis'
  | 'pronunciation_tips'
  | 'interview_practice'
  | 'interview_advanced_feedback'
  | 'interview_unlimited_questions'
  | 'ielts_practice'
  | 'ielts_full_duration'
  | 'ielts_detailed_scoring'
  | 'ielts_personalized_tips';

// Subscription document in Firestore
export interface Subscription {
  userId: string;
  userEmail: string;

  // Plan details
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;

  // Beta user flag - gives limited access to all features
  isBeta?: boolean;

  // Payment provider
  provider: PaymentProvider;

  // Stripe-specific
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;

  // PayPal-specific
  paypalSubscriptionId?: string;
  paypalPlanId?: string;

  // Billing period
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;

  // Trial period
  trialStart?: Date;
  trialEnd?: Date;
  isTrialUsed?: boolean;

  // Regional pricing
  region: PricingRegion;
  currency: Currency;

  // Usage tracking (for free tier limits)
  dailyRecordings: number;
  lastRecordingDate: Date | null;

  // Daily interview tracking (for free tier limits)
  dailyInterviews: number;
  lastInterviewDate: Date | null;

  // Bonus credits from Tetris game (for basic users)
  bonusCredits: number;
  lastBonusCreditDate: Date | null;

  // Beta usage tracking
  betaInterviewsUsed: number;
  betaIeltsExamsUsed: string[]; // Array of exam types used: ['speaking', 'writing', 'reading', 'listening']

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

// Feature access mapping by plan
export const FEATURE_ACCESS: Record<SubscriptionPlan, FeatureKey[]> = {
  free: [],
  basic: ['unlimited_recordings', 'ai_coach'],
  // Pro includes ALL features: Pronunciation + IELTS + Interview
  pro: [
    'unlimited_recordings',
    'ai_coach',
    'phoneme_analysis',
    'prosody_analysis',
    'pronunciation_tips',
    'interview_practice',
    'interview_advanced_feedback',
    'interview_unlimited_questions',
    'ielts_practice',
    'ielts_full_duration',
    'ielts_detailed_scoring',
    'ielts_personalized_tips',
  ],
};

// Trial feature access - limited features during trial
export const TRIAL_FEATURE_ACCESS: Record<SubscriptionPlan, FeatureKey[]> = {
  free: [],
  basic: ['unlimited_recordings', 'ai_coach'],
  // Pro trial includes all features
  pro: [
    'unlimited_recordings',
    'ai_coach',
    'phoneme_analysis',
    'prosody_analysis',
    'pronunciation_tips',
    'interview_practice',
    'interview_advanced_feedback',
    'interview_unlimited_questions',
    'ielts_practice',
    'ielts_full_duration',
    'ielts_detailed_scoring',
    'ielts_personalized_tips',
  ],
};

// Beta user feature access - limited access to all features
// Note: The actual limits are enforced by BETA_LIMITS, not here
// Beta users get feature access but with restrictions (no hell mode, short IELTS only, etc.)
export const BETA_FEATURE_ACCESS: FeatureKey[] = [
  'ai_coach',
  'phoneme_analysis',
  'prosody_analysis',
  'pronunciation_tips',
  'interview_practice',
  // Note: 'interview_advanced_feedback' excluded - no hell mode
  // Note: 'interview_unlimited_questions' excluded - limited to 2 sessions
  'ielts_practice',
  // Note: 'ielts_full_duration' excluded - short versions only
  'ielts_detailed_scoring',
  'ielts_personalized_tips',
];

// Daily recording limit for free tier
export const FREE_TIER_DAILY_LIMIT = 5;

// Daily interview limit for free tier
export const FREE_TIER_DAILY_INTERVIEW_LIMIT = 2;

// Bonus credits configuration (for Tetris game rewards)
export const TETRIS_CREDIT_REWARD = 2; // Credits earned per Tetris game completion
export const MAX_DAILY_BONUS_CREDITS = 6; // Maximum bonus credits per day

// Beta user limits
export const BETA_LIMITS = {
  maxInterviewSessions: 2,      // Beta users can only use interview 2 times total
  allowHellMode: false,          // Beta users cannot use hell mode
  ieltsShortVersionOnly: true,   // Beta users can only use short IELTS versions
  maxIeltsPerType: 1,            // Beta users can only take each IELTS exam type once
};

// IELTS exam types for beta tracking
export type IELTSExamType = 'speaking' | 'writing' | 'reading' | 'listening';

// Plan display names
export const PLAN_NAMES: Record<SubscriptionPlan, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
};

// Pricing tiers configuration
export interface PricingTier {
  id: SubscriptionPlan;
  name: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  pricing: {
    ca: { monthly: number; annual: number };
  };
}

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Get started with basic pronunciation practice',
    features: [
      '5 recordings per day',
      'Basic transcription',
      'Word-level feedback',
    ],
    pricing: {
      ca: { monthly: 0, annual: 0 },
    },
  },
  {
    id: 'basic',
    name: 'Basic',
    description: 'Unlimited pronunciation practice with AI coaching',
    features: [
      'Unlimited recordings',
      'AI Coach feedback',
      'Progress tracking',
      'All Free features',
    ],
    pricing: {
      ca: { monthly: 4.99, annual: 34.99 },
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Complete package with all features included',
    highlighted: true,
    features: [
      'All Basic features',
      'Phoneme-level scoring',
      'Prosody & intonation feedback',
      'IELTS exam preparation',
      'Interview practice mode',
      'Detailed scoring & tips',
    ],
    pricing: {
      ca: { monthly: 9.99, annual: 69.99 },
    },
  },
];

// Currency symbols
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CAD: '$',
};

// Region to currency mapping
export const REGION_CURRENCY: Record<PricingRegion, Currency> = {
  ca: 'CAD',
};

// Region labels
export const REGION_LABELS: Record<PricingRegion, string> = {
  ca: 'Canada',
};
